import { cn } from "@faire/react/tools/cn";
import { tsv } from "@faire/slate/lib/tsv";
import React from "react";

type IProps<C extends React.ElementType> = {
  as?: C;
  children?: React.ReactNode;
  selected?: boolean;
} & React.ComponentPropsWithRef<C>;

export const TabBase = <C extends React.ElementType = "button">({
  className,
  selected,
  children,
  as: Component = "button",
  "data-component-name": dataComponentName,
  ...props
}: IProps<C>) => {
  return (
    // `role="presentation"` removes this visual wrapper from the accessibility
    // tree so the underlying tab becomes a direct child of the tablist. Without
    // it, screen readers do not see the tabs as a set and announce "1 of 1" for
    // each one. The wrapper cannot be dropped because the underline must sit as a
    // sibling of the button, and a button cannot contain a div.
    <div
      className={cn("fs-tab", className)}
      data-component-name={dataComponentName}
      role="presentation"
    >
      <div className="fs-tab__button-underline" aria-hidden="true" />
      <Component
        role="tab"
        aria-selected={!!selected}
        tabIndex={selected ? undefined : -1}
        className={tabButtonStyle.cls()}
        {...props}
      >
        {children}
      </Component>
    </div>
  );
};

const tabButtonStyle = tsv({
  base: "fs-tab__button interactive focusable",
});
